import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Modal,
  Box,
  Group,
  Button,
  Text,
  Badge,
  Center,
  Loader,
  Accordion,
  Alert,
  List,
  Stack,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertCircle, AlertTriangle, Info, RefreshCw } from 'react-feather';
import type { CohortWithDetails } from '../../../../types';
import type { FeatureToggles } from '../../../../services/api/cohorts';
import { getApiErrorMessage } from '../../../../services/api/client';
import { cohortsApi } from '../../../../services/api/cohorts';
import { useEditCohortForm } from './useEditCohortForm';
import { BasicInfoSection } from './BasicInfoSection';
import { GeneralConfigSection } from './GeneralConfigSection';
import { SessionConfigSection } from './SessionConfigSection';
import { ModalitySettingsSection } from './ModalitySettingsSection';
import { TimeSlotOptionsSection } from './TimeSlotOptionsSection';
import { PrimaryContactsSection } from './PrimaryContactsSection';
import { KeyDatesSection } from './KeyDatesSection';
import { TeamAssignmentsSection } from './TeamAssignmentsSection';
import { EmailSettingsSection } from '../settings/EmailSettingsSection';
import { FeatureTogglesSection } from '../settings/FeatureTogglesSection';
import { useFeatureFlag } from '../../../../stores/featureFlags';
import {
  SECTION_LABELS,
  type DestructiveChange,
  type SectionController,
  type SectionKey,
} from './sectionContract';
import { programWeeks } from '../../../../utils/checkinWeeks';
import classes from '../EditCohortDetailsModal.module.css';

export interface EditCohortDetailsModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onUpdate: (cohort: CohortWithDetails) => void;
  initialExpandedSections?: string[];
}

const DEFAULT_EXPANDED_SECTIONS = ['basic-info'];

// Commit order (MED-1269, Flag 1): non-destructive first, the most destructive
// (audience features OFF) last, so an early failure can't leave a cohort with
// emails suppressed and nothing else applied.
const SAVE_RESULT_TOAST_ID = 'cohort-save-result';

const SECTION_ACCORDION_VALUE: Record<SectionKey, string> = {
  emailSettings: 'email-settings',
  timeSlots: 'time-slots',
  featureToggles: 'feature-toggles',
};

const COMMIT_ORDER: SectionKey[] = [
  'emailSettings',
  'timeSlots',
  'featureToggles',
];

export function EditCohortDetailsModal({
  opened,
  onClose,
  cohort,
  onUpdate,
  initialExpandedSections,
}: EditCohortDetailsModalProps) {
  const {
    form,
    isLoading,
    coreDirty,
    contacts,
    selectedContacts,
    availableContacts,
    handleAddContact,
    handleRemoveContact,
    teamAssignments,
    handleAssignmentChange,
    handleAutoPopulate,
    userOptions,
    jobTitleGroups,
    dateConstraints,
    addDateConstraint,
    removeDateConstraint,
    updateDateConstraint,
    coachHuddles,
    addCoachHuddle,
    removeCoachHuddle,
    updateCoachHuddle,
    sessionDates,
    updateSessionDate,
    sessionDatesLocked,
    totalSessionCount,
    uniqueSessionNumbers,
    modalityConfigs,
    updateModalityConfig,
    updateModalitySessionConfigs,
    updateModalitySettingsField,
    canEditSessionConfig,
    showDurationWarning,
    hasDateChanges,
    hasTeamAssignmentChanges,
    wrapDateConflict,
    validateCore,
    commitCore,
  } = useEditCohortForm({ opened, cohort, onUpdate, onClose });

  const hasAnyRegeneratableChanges = hasDateChanges || hasTeamAssignmentChanges;

  // MED-887: Hide Modality Settings while the Live Session Delivery flag is off
  // (or still loading) — the underlying PATCH endpoint is gated and returns 404.
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  // MED-778: pending feature-toggle intent, so EmailSettingsSection's lock UI
  // reflects what the user is about to save (AC15).
  const [featureToggles, setFeatureToggles] = useState<FeatureToggles | null>(
    null
  );

  // MED-1269: section controllers for the three converted sections. Refs (not
  // state) — read at save/close time; identity-stable registrars avoid loops.
  const sectionRefs = useRef<Record<SectionKey, SectionController | null>>({
    emailSettings: null,
    timeSlots: null,
    featureToggles: null,
  });
  const registerEmail = useCallback((c: SectionController | null) => {
    sectionRefs.current.emailSettings = c;
  }, []);
  const registerTimeSlots = useCallback((c: SectionController | null) => {
    sectionRefs.current.timeSlots = c;
  }, []);
  const registerFeature = useCallback((c: SectionController | null) => {
    sectionRefs.current.featureToggles = c;
  }, []);

  const [savingState, setSavingState] = useState<null | 'save' | 'regenerate'>(
    null
  );
  const [confirm, setConfirm] = useState<{
    items: DestructiveChange[];
    due: boolean;
    team: boolean;
  } | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  // Controlled so a blocked save can open the section that holds the error.
  const [expanded, setExpanded] = useState<string[]>(
    initialExpandedSections ?? DEFAULT_EXPANDED_SECTIONS
  );
  useEffect(() => {
    if (opened) {
      setExpanded(initialExpandedSections ?? DEFAULT_EXPANDED_SECTIONS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const anyDirty = useCallback((): boolean => {
    if (coreDirty) return true;
    return COMMIT_ORDER.some(k => sectionRefs.current[k]?.dirty);
  }, [coreDirty]);

  const collectDestructive = (): DestructiveChange[] => {
    const items: DestructiveChange[] = [];
    for (const k of COMMIT_ORDER) {
      const c = sectionRefs.current[k];
      if (c?.dirty) items.push(...c.destructive);
    }
    return items;
  };

  // Attempt every section, collect per-section outcomes (AC19). Core is
  // validated up front so a bad form never triggers partial writes.
  const runSave = useCallback(
    async (regenerateDueDates: boolean, regenerateTeamAssignments: boolean) => {
      setSavingState(
        regenerateDueDates || regenerateTeamAssignments ? 'regenerate' : 'save'
      );
      const saved: string[] = [];
      const failed: string[] = [];
      let coreMessages: string[] = [];

      try {
        coreMessages = await commitCore(
          regenerateDueDates,
          regenerateTeamAssignments
        );
        saved.push(SECTION_LABELS.core);
      } catch (error) {
        failed.push(
          `${SECTION_LABELS.core} (${getApiErrorMessage(error, 'failed')})`
        );
      }

      for (const key of COMMIT_ORDER) {
        const controller = sectionRefs.current[key];
        if (!controller?.dirty) continue;
        try {
          await controller.commit();
          saved.push(SECTION_LABELS[key]);
        } catch (error) {
          failed.push(
            `${SECTION_LABELS[key]} (${getApiErrorMessage(error, 'failed')})`
          );
        }
      }

      // The page copy of the cohort predates the feature-toggle PATCH. One
      // re-fetch lets the cohort page banners reflect the save without a reload.
      if (saved.includes(SECTION_LABELS.featureToggles)) {
        try {
          onUpdate(await cohortsApi.getById(cohort.id));
        } catch {
          // The save itself succeeded; the page shows the new state on reload.
        }
      }

      notifications.hide(SAVE_RESULT_TOAST_ID);
      if (failed.length === 0) {
        const extra =
          coreMessages.length > 0 ? ` ${coreMessages.join(' and ')}.` : '';
        notifications.show({
          id: SAVE_RESULT_TOAST_ID,
          title: 'Saved',
          message: `Cohort updated successfully.${extra}`,
          color: 'green',
        });
        setSavingState(null);
        onClose();
        return;
      }

      // Replace any earlier save result so two toasts never stack.
      notifications.hide(SAVE_RESULT_TOAST_ID);
      // AC19: partial failure — report what saved and what did not, keep open.
      notifications.show({
        id: SAVE_RESULT_TOAST_ID,
        title: 'Some changes did not save',
        message:
          (saved.length ? `Saved: ${saved.join(', ')}. ` : '') +
          `Not saved: ${failed.join(', ')}. Your unsaved changes are still here. Try Save again.`,
        color: 'red',
        autoClose: false,
      });
      setSavingState(null);
    },
    [commitCore, onClose, onUpdate, cohort.id]
  );

  const handleSaveClick = useCallback(
    (regenerateDueDates: boolean, regenerateTeamAssignments: boolean) => {
      if (!validateCore()) return;
      // MED-1307: a section may refuse its pending state (bad sender address).
      const blocked = COMMIT_ORDER.filter(k => {
        const c = sectionRefs.current[k];
        return c?.validate ? !c.validate() : false;
      });
      if (blocked.length > 0) {
        setExpanded(prev => [
          ...prev,
          ...blocked
            .map(k => SECTION_ACCORDION_VALUE[k])
            .filter(v => !prev.includes(v)),
        ]);
        return;
      }
      const destructive = collectDestructive();
      if (destructive.length > 0) {
        // AC11–AC14: one future-tense summary confirmation for all destructive changes.
        setConfirm({
          items: destructive,
          due: regenerateDueDates,
          team: regenerateTeamAssignments,
        });
        return;
      }
      runSave(regenerateDueDates, regenerateTeamAssignments);
    },
    [validateCore, runSave]
  );

  // AC7–AC10: guard Cancel / Escape / backdrop when there are pending changes.
  const requestClose = useCallback(() => {
    if (savingState) return; // don't interrupt an in-flight save
    if (anyDirty()) {
      setDiscardOpen(true);
      return;
    }
    onClose();
  }, [savingState, anyDirty, onClose]);

  return (
    <Modal
      opened={opened}
      onClose={requestClose}
      title={
        <Group gap="md">
          <Text fw={600} size="lg">
            Edit Cohort Details
          </Text>
          {cohort.code && (
            <Badge variant="light" color="gray" size="lg">
              {cohort.code}
            </Badge>
          )}
        </Group>
      }
      size="90%"
      styles={{
        body: { padding: 0 },
        header: {
          borderBottom: '1px solid var(--mantine-color-gray-3)',
          padding: '16px 24px',
        },
      }}
    >
      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <Box className={classes.modalContent}>
          <Box className={classes.scrollableContent}>
            {Object.keys(form.errors).length > 0 && (
              <Alert
                icon={<AlertCircle size={18} />}
                color="red"
                variant="light"
                mb="md"
              >
                <Text size="sm" fw={600} mb={4}>
                  Please fix the following errors:
                </Text>
                <Box component="ul" style={{ margin: 0, paddingLeft: 16 }}>
                  {Object.values(form.errors).map((error, index) => (
                    <li key={index}>
                      <Text size="sm">{error}</Text>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            <Accordion
              multiple
              value={expanded}
              onChange={setExpanded}
              classNames={{
                item: classes.accordionItem,
                control: classes.accordionControl,
                label: classes.accordionLabel,
                content: classes.accordionContent,
              }}
            >
              <BasicInfoSection form={form} />

              <GeneralConfigSection form={form} />

              <SessionConfigSection
                modalityConfigs={modalityConfigs}
                canEditSessionConfig={canEditSessionConfig}
                showDurationWarning={showDurationWarning}
                updateModalityConfig={updateModalityConfig}
                updateModalitySessionConfigs={updateModalitySessionConfigs}
                checkins={form.values.checkins}
                onCheckinsChange={(next) => {
                  form.setFieldValue('checkins', next);
                  form.clearFieldError('checkins');
                }}
                programWeeks={programWeeks(form.values.start_date, form.values.end_date)}
                startDate={form.values.start_date}
                checkinsError={typeof form.errors.checkins === 'string' ? form.errors.checkins : null}
              />

              {/* MED-848 — AC8/AC9 post-creation modality settings */}
              {/* MED-887 — hide while live_session_delivery flag is off (gated endpoint) */}
              {liveSessionDeliveryEnabled && (
                <ModalitySettingsSection
                  modalityConfigs={modalityConfigs}
                  updateModalitySettingsField={updateModalitySettingsField}
                />
              )}

              <TimeSlotOptionsSection
                cohortId={cohort.id}
                cohortCode={cohort.code}
                registerSection={registerTimeSlots}
              />

              <PrimaryContactsSection
                selectedContacts={selectedContacts}
                availableContacts={availableContacts}
                contacts={contacts}
                handleAddContact={handleAddContact}
                handleRemoveContact={handleRemoveContact}
                classes={classes}
              />

              <KeyDatesSection
                form={form}
                dateConstraints={dateConstraints}
                addDateConstraint={addDateConstraint}
                removeDateConstraint={removeDateConstraint}
                updateDateConstraint={updateDateConstraint}
                coachHuddles={coachHuddles}
                addCoachHuddle={addCoachHuddle}
                removeCoachHuddle={removeCoachHuddle}
                updateCoachHuddle={updateCoachHuddle}
                sessionDates={sessionDates}
                updateSessionDate={updateSessionDate}
                sessionDatesLocked={sessionDatesLocked}
                totalSessionCount={totalSessionCount}
                uniqueSessionNumbers={uniqueSessionNumbers}
                wrapDateConflict={wrapDateConflict}
                classes={classes}
              />

              <TeamAssignmentsSection
                teamAssignments={teamAssignments}
                handleAssignmentChange={handleAssignmentChange}
                userOptions={userOptions}
                jobTitleGroups={jobTitleGroups}
                classes={classes}
                onAutoPopulate={handleAutoPopulate}
              />

              <FeatureTogglesSection
                cohortId={cohort.id}
                onToggleChange={setFeatureToggles}
                registerSection={registerFeature}
              />

              <EmailSettingsSection
                cohortId={cohort.id}
                externalFeatureToggles={featureToggles}
                registerSection={registerEmail}
              />
            </Accordion>
          </Box>

          {/* Regeneration Callout */}
          {hasAnyRegeneratableChanges && (
            <Alert
              icon={<Info size={18} />}
              title={
                hasDateChanges && hasTeamAssignmentChanges
                  ? 'Regenerate Tasks'
                  : hasTeamAssignmentChanges
                    ? 'Regenerate Task Assignments'
                    : 'Regenerate Task Due Dates'
              }
              color="blue"
              className={classes.dateChangeCallout}
            >
              {hasDateChanges && (
                <Text size="sm">
                  If you choose to regenerate due dates, tasks that have been
                  manually edited will not be updated. Only tasks with
                  auto-calculated due dates will be recalculated based on the
                  new dates.
                </Text>
              )}
              {hasTeamAssignmentChanges && (
                <Text size="sm" mt={hasDateChanges ? 'xs' : 0}>
                  If you choose to regenerate task assignments, only tasks that
                  were auto-assigned from team roles will be updated. Tasks that
                  have been manually reassigned will not be changed.
                </Text>
              )}
            </Alert>
          )}

          {/* Footer */}
          <Box className={classes.footer}>
            <Group justify="space-between">
              <Button
                variant="default"
                onClick={requestClose}
                disabled={savingState !== null}
              >
                Cancel
              </Button>
              <Group>
                {hasAnyRegeneratableChanges ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleSaveClick(false, false)}
                      loading={savingState === 'save'}
                      disabled={savingState === 'regenerate'}
                    >
                      Save Without Regenerating
                    </Button>
                    <Button
                      leftSection={<RefreshCw size={16} />}
                      onClick={() =>
                        handleSaveClick(
                          hasDateChanges,
                          hasTeamAssignmentChanges
                        )
                      }
                      loading={savingState === 'regenerate'}
                      disabled={savingState === 'save'}
                    >
                      {hasDateChanges && hasTeamAssignmentChanges
                        ? 'Save & Regenerate'
                        : hasTeamAssignmentChanges
                          ? 'Save & Regenerate Task Assignments'
                          : 'Save & Regenerate Due Dates'}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => handleSaveClick(false, false)}
                    loading={savingState === 'save'}
                  >
                    Save Changes
                  </Button>
                )}
              </Group>
            </Group>
          </Box>
        </Box>
      )}

      {/* AC11–AC14: single future-tense summary confirmation for destructive changes */}
      <Modal
        opened={confirm !== null}
        onClose={() => setConfirm(null)}
        title={
          <Group gap="xs">
            <AlertTriangle
              size={20}
              color="var(--mantine-color-warningAmber-6)"
            />
            <Text fw={600}>Confirm changes</Text>
          </Group>
        }
        size="md"
        zIndex={1100}
      >
        <Stack gap="md">
          <Text size="sm">Saving will make the following changes:</Text>
          <List spacing="xs" size="sm">
            {confirm?.items.map((item, i) => (
              <List.Item key={i}>
                <Text size="sm" fw={500}>
                  {item.label}
                </Text>
                {item.details && item.details.length > 0 && (
                  <List withPadding size="sm" c="gray.7">
                    {item.details.map((d, j) => (
                      <List.Item key={j}>{d}</List.Item>
                    ))}
                  </List>
                )}
              </List.Item>
            ))}
          </List>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setConfirm(null)}>
              Go back
            </Button>
            <Button
              color="warningAmber"
              onClick={() => {
                const c = confirm;
                setConfirm(null);
                if (c) runSave(c.due, c.team);
              }}
            >
              Save changes
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* AC7–AC10: unsaved-changes discard prompt */}
      <Modal
        opened={discardOpen}
        onClose={() => setDiscardOpen(false)}
        title={
          <Group gap="xs">
            <AlertTriangle
              size={20}
              color="var(--mantine-color-warningAmber-6)"
            />
            <Text fw={600}>Discard unsaved changes?</Text>
          </Group>
        }
        size="md"
        zIndex={1100}
      >
        <Stack gap="md">
          <Text size="sm">
            You have unsaved changes in this modal. Closing now will discard
            them — nothing has been saved yet.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setDiscardOpen(false)}>
              Keep editing
            </Button>
            <Button
              color="red"
              onClick={() => {
                setDiscardOpen(false);
                onClose();
              }}
            >
              Discard changes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}

export default EditCohortDetailsModal;
